"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useChatAutoScroll = exports.SPACER_MIN = void 0;
var react_1 = require("react");
exports.SPACER_MIN = 64;
/**
 * Hook to manage auto-scroll positioning during chat streaming.
 *
 * When streaming begins, this hook:
 * 1. Waits for the pending user message to render
 * 2. Measures the message height
 * 3. Calculates a spacer height to position the message at the top
 * 4. Scrolls the container to show the message at the top
 *
 * When streaming ends, the spacer shrinks to its visible portion (minimum 64px)
 * to prevent visual jumping.
 *
 * @example
 * ```tsx
 * const {
 *   spacerHeight,
 *   containerRef,
 *   pendingMessageRef,
 *   showStreamingUI,
 *   spacerRef,
 * } = useChatAutoScroll({
 *   isStreaming,
 *   messagesLength: messages.length,
 * })
 *
 * return (
 *   <div ref={containerRef}>
 *     {messages.map(...)}
 *     {showStreamingUI && (
 *       <div ref={pendingMessageRef}>
 *         <UserMessage ... />
 *         <StreamingResponse ... />
 *       </div>
 *     )}
 *     <div ref={spacerRef} style={{ height: spacerHeight }} />
 *   </div>
 * )
 * ```
 */
var useChatAutoScroll = function (_a) {
    var isStreaming = _a.isStreaming, messagesLength = _a.messagesLength;
    var containerRef = (0, react_1.useRef)(null);
    var pendingMessageRef = (0, react_1.useRef)(null);
    var spacerRef = (0, react_1.useRef)(null);
    var _b = (0, react_1.useState)('IDLE'), scrollState = _b[0], setScrollState = _b[1];
    var _c = (0, react_1.useState)(exports.SPACER_MIN), spacerHeight = _c[0], setSpacerHeight = _c[1];
    var prevMessagesLength = (0, react_1.useRef)(messagesLength !== null && messagesLength !== void 0 ? messagesLength : 0);
    // Track if completion detected for current streaming session (reset when streaming starts)
    var hasCompletedRef = (0, react_1.useRef)(false);
    // Derive showStreamingUI during render (not in effect!) to prevent duplicate content
    // If we're streaming and messages just increased, completion happened - hide
    // the streaming UI immediately
    var currentLength = messagesLength !== null && messagesLength !== void 0 ? messagesLength : 0;
    var messagesJustIncreased = currentLength > prevMessagesLength.current;
    if (messagesJustIncreased && scrollState === 'STREAMING') {
        hasCompletedRef.current = true;
    }
    // showStreamingUI is true only during active streaming states AND completion hasn't been detected
    var showStreamingUI = !hasCompletedRef.current && (scrollState === 'USER_MSG'
        || scrollState === 'SPACER_SIZING'
        || scrollState === 'SCROLLING'
        || scrollState === 'STREAMING');
    // Entry transition: detect streaming start
    (0, react_1.useEffect)(function () {
        if (isStreaming && scrollState === 'IDLE') {
            hasCompletedRef.current = false; // Reset completion flag for new streaming session
            setScrollState('USER_MSG');
        }
    }, [isStreaming, scrollState]);
    // Main state machine effect
    (0, react_1.useEffect)(function () {
        switch (scrollState) {
            case 'USER_MSG':
                // Wait for pending message to render, then transition
                requestAnimationFrame(function () {
                    setScrollState('SPACER_SIZING');
                });
                break;
            case 'SPACER_SIZING':
                // Measure pending message and calculate spacer
                if (containerRef.current && pendingMessageRef.current) {
                    var containerHeight = containerRef.current.clientHeight;
                    var messageHeight = pendingMessageRef.current.offsetHeight;
                    var newSpacerHeight = Math.max(containerHeight - messageHeight - 16, 0);
                    setSpacerHeight(newSpacerHeight);
                    // Transition happens in spacerHeight effect below
                }
                break;
            case 'SCROLLING':
                // Scroll to bottom, then transition to streaming
                requestAnimationFrame(function () {
                    if (containerRef.current) {
                        containerRef.current.scrollTo({
                            top: containerRef.current.scrollHeight,
                            behavior: 'smooth',
                        });
                    }
                    setScrollState('STREAMING');
                });
                break;
            case 'STREAMING':
            case 'IDLE':
                // No action needed
                break;
            case 'COMPLETING':
                // Brief state to coordinate the transition, then shrink spacer
                requestAnimationFrame(function () {
                    setScrollState('SHRINKING');
                });
                break;
            case 'SHRINKING':
                // Shrink spacer to only the visible portion (no visual jump)
                requestAnimationFrame(function () {
                    if (containerRef.current && spacerRef.current) {
                        var container = containerRef.current;
                        var spacer = spacerRef.current;
                        var viewportBottom = container.scrollTop + container.clientHeight;
                        var spacerTop = spacer.offsetTop;
                        // Only keep the visible portion of spacer, or 64px, whichever is larger
                        var visibleSpacerHeight = Math.max(64, viewportBottom - spacerTop);
                        setSpacerHeight(visibleSpacerHeight);
                    }
                    setScrollState('IDLE');
                });
                break;
            default:
                break;
        }
    }, [scrollState]);
    // Spacer height change triggers scroll transition
    (0, react_1.useEffect)(function () {
        if (spacerHeight > 0 && scrollState === 'SPACER_SIZING') {
            // Spacer has been set, wait for render then scroll
            requestAnimationFrame(function () {
                setScrollState('SCROLLING');
            });
        }
    }, [spacerHeight, scrollState]);
    // Transition state machine when messages increase during streaming
    // Note: showStreamingUI is derived during render and already false by now
    (0, react_1.useEffect)(function () {
        if (scrollState === 'STREAMING' && hasCompletedRef.current) {
            setScrollState('COMPLETING');
        }
    }, [messagesLength, scrollState]);
    // Update prevMessagesLength ref after each render
    (0, react_1.useEffect)(function () {
        prevMessagesLength.current = messagesLength !== null && messagesLength !== void 0 ? messagesLength : 0;
    }, [messagesLength]);
    // Exit transition: streaming stopped
    (0, react_1.useEffect)(function () {
        if (scrollState === 'STREAMING' && !isStreaming) {
            setScrollState('IDLE');
        }
    }, [isStreaming, scrollState]);
    return {
        spacerHeight: spacerHeight,
        containerRef: containerRef,
        pendingMessageRef: pendingMessageRef,
        showStreamingUI: showStreamingUI,
        spacerRef: spacerRef,
    };
};
exports.useChatAutoScroll = useChatAutoScroll;
